import type { GameHold, GameHoldsResponse } from "@pax/api-contract/accounting/v1alpha1";
import { useInfiniteQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { Problem } from "@/features/staff/components";
import { gameHoldChanges, type HistoryWindow, type SpendBy, startingWith } from "./balances";
import { Note, type PageSize } from "./components";
import { type GameHoldsFilter, gameHoldsDaysQuery, gameHoldsQuery } from "./queries";
import {
  type DayRange,
  dragZoom,
  type GraphFrame,
  type GraphSettings,
  type GraphSpan,
  rangeSteps,
  SpendBars,
  SpendByTabs,
  stepFor,
  TimeGraph,
  useDaysCovering,
  zoomedSpan,
} from "./TimeGraph";

const SPEND_BY: readonly SpendBy[] = ["player", "bucket"];

/** Picks a span of time, and the start of the game's holds that shows it. */
type SelectHolds = (window: HistoryWindow, start: string) => void;

interface GameGraphProps {
  gameId: string;
  by: SpendBy;
  frame: GraphFrame;
  onSelect?: SelectHolds;
  onHighlight?: (window: HistoryWindow) => void;
}

/**
 * What a game's holds spent over time, by who paid or by the balance it came from. Loaded pages
 * show only what the holds list has read.
 */
export function GameSpendGraph({
  gameId,
  filter,
  pageSize,
  settings,
  onSettingsChange,
  onSelect,
  onHighlight,
}: {
  gameId: string;
  filter: GameHoldsFilter;
  pageSize: PageSize;
  settings: GraphSettings;
  onSettingsChange: (patch: Partial<GraphSettings>) => void;
  onSelect?: SelectHolds;
  /** The span of a bar a click zoomed into. */
  onHighlight?: (window: HistoryWindow) => void;
}) {
  const by = settings.spendBy && SPEND_BY.includes(settings.spendBy) ? settings.spendBy : "player";
  return (
    <TimeGraph
      settings={settings}
      onSettingsChange={onSettingsChange}
      controls={
        <SpendByTabs
          options={SPEND_BY}
          value={by}
          onChange={next => onSettingsChange({ spendBy: next === "player" ? undefined : next })}
        />
      }
    >
      {frame =>
        frame.range === "loaded" || !frame.period ? (
          <LoadedHoldsGraph
            gameId={gameId}
            filter={filter}
            pageSize={pageSize}
            by={by}
            frame={frame}
            onSelect={onSelect}
            onHighlight={onHighlight}
          />
        ) : (
          <DaysHoldsGraph
            key={frame.range}
            gameId={gameId}
            range={frame.range}
            period={frame.period}
            by={by}
            frame={frame}
            onSelect={onSelect}
            onHighlight={onHighlight}
          />
        )
      }
    </TimeGraph>
  );
}

/**
 * The cursors of the hold pages already read with this filter and page size, newest first, joined
 * with `|`, as far back as they run unbroken from the newest.
 */
function useLoadedCursors(gameId: string, filter: GameHoldsFilter, pageSize: PageSize) {
  const client = useQueryClient();
  const read = () => {
    const cursors: string[] = [""];
    for (;;) {
      const before = cursors.at(-1) || undefined;
      const page = client.getQueryData<GameHoldsResponse>(
        gameHoldsQuery(gameId, filter, { before, limit: pageSize }).queryKey,
      );
      const next = page?.nextBeforeHoldId;
      if (!next || cursors.includes(next)) return cursors.join("|");
      cursors.push(next);
    }
  };
  return useSyncExternalStore(listener => client.getQueryCache().subscribe(listener), read);
}

function LoadedHoldsGraph({
  filter,
  pageSize,
  ...props
}: GameGraphProps & { filter: GameHoldsFilter; pageSize: PageSize }) {
  const { gameId } = props;
  const cursors = useLoadedCursors(gameId, filter, pageSize).split("|");
  const pages = useQueries({
    queries: cursors.map((cursor, index) => ({
      ...gameHoldsQuery(gameId, filter, { before: cursor || undefined, limit: pageSize }),
      enabled: index === 0,
      staleTime: Number.POSITIVE_INFINITY,
    })),
  });
  const loaded = pages.flatMap(page => (page.data ? [page.data] : []));
  if (loaded.length === 0) return <Note>Loading holds…</Note>;
  const holds = loaded.flatMap(page => page.holds);
  const oldest = holds.at(-1);
  if (!oldest) return <Note>No holds</Note>;
  const oldestAt = Date.parse(oldest.placedAt);
  const now = Date.now();
  const reachedStart = loaded.at(-1)?.nextBeforeHoldId === null;
  return (
    <GameChart
      {...props}
      holds={holds}
      span={{
        from: oldestAt,
        knownFrom: reachedStart ? Number.NEGATIVE_INFINITY : oldestAt,
        step: stepFor(now - oldestAt),
        to: now,
        now,
        stepped: false,
        progress: null,
      }}
    />
  );
}

/** Reads the days the period needs, then adds up what they spent. */
function DaysHoldsGraph({
  range,
  period: { from, to },
  ...props
}: GameGraphProps & {
  range: DayRange;
  period: { from: number; to: number };
}) {
  const { now, stepped, zoom } = props.frame;
  const today = new Date(now).toISOString().slice(0, 10);
  const days = useInfiniteQuery(gameHoldsDaysQuery(props.gameId, today));
  const { read, needed, knownFrom, progress } = useDaysCovering(days, today, from, zoom);
  if (days.error) return <Problem error={days.error} />;
  if (read.length === 0) return <Note>Loading day 1 of {needed}…</Note>;
  return (
    <GameChart
      {...props}
      holds={read.flatMap(day => day.holds)}
      span={{ from, knownFrom, step: rangeSteps[range], to, now, stepped, progress }}
    />
  );
}

function GameChart({
  gameId,
  by,
  frame,
  onSelect,
  onHighlight,
  holds,
  span,
}: GameGraphProps & { holds: readonly GameHold[]; span: GraphSpan }) {
  const { shown, note } = zoomedSpan(span, frame.zoom);
  if (note) return note;
  return (
    <SpendBars
      {...shown}
      changes={holds.flatMap(hold => gameHoldChanges(gameId, hold))}
      by={by}
      fit={frame.fit}
      onFitChange={frame.onFitChange}
      onSelect={
        onSelect &&
        (window => {
          const newest = holds.find(hold => Date.parse(hold.placedAt) < window.to);
          if (newest) onSelect(window, startingWith(newest.holdId));
        })
      }
      onHighlight={onHighlight}
      onZoom={dragZoom(frame.onZoom)}
    />
  );
}
